import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Space, Tag, Tooltip, Modal, message, Card, theme, List, Typography, Pagination, Input } from 'antd';
import { EditOutlined, DeleteOutlined, HomeOutlined, PlusOutlined, LoadingOutlined, StarOutlined, NotificationOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { TENANT_API, FACTORY_API, INDUSTRY_API } from '../constants/api';
import axiosInstance from '../services/axiosConfig';
import TenantForm from '../components/tenants/TenantForm';
import ActionButtons from '../components/common/ActionButtons';

function TenantList() {
  const [data, setData] = useState({ items: [], total: 0, currentPage: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    search: '',
  });
  const [industries, setIndustries] = useState([]);
  const [factories, setFactories] = useState([]);
  const resizeTimeoutRef = useRef(null);
  const { token } = theme.useToken();
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationPage, setNotificationPage] = useState(1);
  const [notificationTotal, setNotificationTotal] = useState(0);
  const [notificationPageSize] = useState(5);
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const searchTimeoutRef = useRef(null);

  // Lấy danh sách ngành nghề
  const fetchIndustries = useCallback(async () => {
    try {
      const response = await axiosInstance.get(INDUSTRY_API.LIST);
      setIndustries(response.data.industries || []);
    } catch (error) {
      message.error('Không thể tải danh sách ngành nghề');
    }
  }, []);

  // Lấy danh sách nhà xưởng
  const fetchFactories = useCallback(async () => {
    try {
      const response = await axiosInstance.get(FACTORY_API.LIST);
      setFactories(response.data.factories || []);
    } catch (error) {
      message.error('Không thể tải danh sách nhà xưởng');
    }
  }, []);

  // Lấy danh sách khách thuê
  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] !== undefined && queryParams[key] !== '') {
          params.append(key, queryParams[key]);
        }
      });

      const response = await axiosInstance.get(`${TENANT_API.LIST}?${params.toString()}`);

      // Lấy thông tin contracts cho mỗi tenant
      const tenantsWithContracts = await Promise.all(
        (response.data.tenants || []).map(async (tenant) => {
          try {
            const contractsResponse = await axiosInstance.get(`${TENANT_API.LIST}/${tenant.TenantID}/contracts`);
            return {
              ...tenant,
              Contracts: contractsResponse.data.contracts || []
            };
          } catch (error) {
            console.error(`Error fetching contracts for tenant ${tenant.TenantID}:`, error);
            return {
              ...tenant,
              Contracts: []
            };
          }
        })
      );

      setData({
        items: tenantsWithContracts,
        total: response.data.total || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      message.error('Không thể tải danh sách khách thuê');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchIndustries();
    fetchFactories();
    fetchTenants();
  }, [fetchIndustries, fetchFactories, fetchTenants]);

  // Update the resize handling section
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Increase the timeout to reduce frequency of updates
    resizeTimeoutRef.current = setTimeout(() => {
      setWindowWidth(window.innerWidth);
    }, 500); // Increased to 500ms for better debouncing
  }, []);

  useEffect(() => {
    // Patching at global level to suppress ResizeObserver errors
    const originalError = window.console.error;
    window.console.error = function (message) {
      if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
        // Suppress ResizeObserver loop errors completely
        return;
      }
      originalError.apply(this, arguments);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      // Restore original console.error
      window.console.error = originalError;
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  const columns = [
    {
      title: 'Mã KH',
      dataIndex: 'TenantID',
      key: 'TenantID',
      width: isMobile ? 80 : 100,
      fixed: 'left',
    },
    {
      title: 'Tên KH',
      dataIndex: 'TenantName',
      key: 'TenantName',
      width: isMobile ? 350 : 450,
      ellipsis: true,
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'Address',
      key: 'Address',
      width: isMobile ? 100 : 120,
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'Phone',
      key: 'Phone',
      width: isMobile ? 100 : 120,
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      width: isMobile ? 160 : 200,
      ellipsis: true,
    },
    {
      title: 'Ngành nghề',
      dataIndex: 'BusinessType',
      key: 'BusinessType',
      width: isMobile ? 120 : 150,
      render: (BusinessType) => {
        const industry = industries.find(i => i.IndustryID === BusinessType);
        return industry ? industry.IndustryName : '';
      }
    },
    {
      title: 'Hạn thanh toán',
      dataIndex: 'PaymentDueDate',
      key: 'PaymentDueDate',
      width: isMobile ? 100 : 120,

    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      width: isMobile ? 100 : 120,
      render: (Status) => (
        <Tag icon={<StarOutlined />} color={Status ? 'green' : 'red'} style={{ margin: 0 }}>
          {Status ? 'Hoạt động' : 'Ngừng hoạt động'}
        </Tag>
      )
    },
    {
      title: 'Nhà xưởng đang thuê',
      dataIndex: 'Contracts',
      key: 'Contracts',
      width: isMobile ? 140 : 180,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        // Lấy ID nhà xưởng đầu tiên hoặc chuỗi rỗng nếu không có
        const aFactoryID = (a.Contracts && a.Contracts[0]?.Factory?.FactoryID) ||
          (a.Contracts && a.Contracts[0]?.FactoryID) || '';
        const bFactoryID = (b.Contracts && b.Contracts[0]?.Factory?.FactoryID) ||
          (b.Contracts && b.Contracts[0]?.FactoryID) || '';
        return aFactoryID.localeCompare(bFactoryID);
      },
      render: (Contracts) => (
        <Space wrap size={[4, 4]} style={{ display: 'flex', justifyContent: 'flex-start' }}>
          {(Contracts || []).map(contract => {
            const factory = contract.Factory || {};
            return (
              <Tooltip
                key={contract.ContractID}
                title={
                  <div>
                    <div>{factory.FactoryName} - {factory.Area}m²</div>
                    <div>Từ: {new Date(contract.StartDate).toLocaleDateString()}</div>
                    <div>Đến: {new Date(contract.EndDate).toLocaleDateString()}</div>
                    <div>Giá thuê: {contract.RentAmount?.toLocaleString()} VNĐ</div>
                    <div>Trạng thái: {contract.Status}</div>
                  </div>
                }
              >
                <Tag icon={<HomeOutlined />} color="blue" style={{ margin: 0 }}>
                  {factory.FactoryName || contract.FactoryName}
                </Tag>
              </Tooltip>
            );
          })}
        </Space>
      )
    },
    {
      title: 'Ghi chú',
      dataIndex: 'Note',
      key: 'Note',
      width: isMobile ? 140 : 180,
      ellipsis: {
        showTitle: false,
      },
      render: Note => (
        <Tooltip placement="topLeft" title={Note}>
          {Note}
        </Tooltip>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: isMobile ? 100 : 130,
      align: 'center',
      render: (_, record) => (
        <Space size={isMobile ? 4 : 8} style={{ display: 'flex', justifyContent: 'center', }}>
          <ActionButtons
            module="tenants"
            showAdd={false}
            showView={false}
            onEdit={() => handleEdit(record)}
            onDelete={() => handleDelete(record)}
            record={record}
          />

          <Button
            type="default"
            icon={<NotificationOutlined />}
            onClick={() => handleViewNotifications(record)}
            size={isMobile ? "small" : "middle"}
            style={{ padding: isMobile ? '0 8px' : '4px 8px' }}
          >
          </Button>

        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingTenant(null);
    setIsModalVisible(true);
  };

  const handleEdit = async (record) => {
    try {
      // Tạm thời chỉ gọi API lấy thông tin tenant
      const response = await axiosInstance.get(TENANT_API.DETAIL(record.TenantID));
      setEditingTenant(response.data);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error:', error);
      message.error('Không thể tải thông tin khách thuê');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa khách hàng ${record.TenantName}?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await axiosInstance.delete(TENANT_API.DELETE(record.TenantID));
          message.success('Xóa khách hàng thành công!');
          fetchTenants();
        } catch (error) {
          message.error('Có lỗi xảy ra khi xóa khách hàng');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        Status: values.Status ?? true,
      };

      if (editingTenant) {
        await axiosInstance.put(TENANT_API.UPDATE(editingTenant.TenantID), submitData);
        console.log(submitData);
        message.success('Cập nhật khách hàng thành công!');
      } else {
        await axiosInstance.post(TENANT_API.CREATE, submitData);
        message.success('Thêm khách hàng thành công!');
      }
      setIsModalVisible(false);
      fetchTenants();
    } catch (error) {
      message.error('Có lỗi xảy ra: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchNotifications = async (tenantId, page = 1) => {
    setLoadingNotifications(true);
    try {
      const response = await axiosInstance.get(`${TENANT_API.LIST}/${tenantId}/notifications?page=${page}&limit=${notificationPageSize}`);
      if (response.data && response.data.notifications) {
        setNotifications(response.data.notifications);
        setNotificationTotal(response.data.total || 0);
        setNotificationPage(response.data.currentPage || 1);
      }
    } catch (error) {
      message.error('Không thể tải danh sách thông báo');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleViewNotifications = async (record) => {
    setSelectedTenant(record);
    setNotificationModalVisible(true);
    await fetchNotifications(record.TenantID, 1);
  };

  const handleNotificationPageChange = async (page) => {
    if (selectedTenant) {
      await fetchNotifications(selectedTenant.TenantID, page);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        search: value,
        page: 1 // Reset to first page when searching
      }));
    }, 500); // 500ms delay
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setQueryParams(prev => ({
      ...prev,
      search: '',
      page: 1
    }));
  };

  return (
    <div style={{
      padding: '16px',
      backgroundColor: token.colorBgContainer
    }}>
      <Card
        title={<h2 style={{ margin: 0 }}>Danh Sách Khách Thuê</h2>}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size={isMobile ? "small" : "middle"}
          >
            {!isMobile && "Thêm khách thuê mới"}
          </Button>
        }
        bodyStyle={{ padding: isMobile ? 8 : 16 }}
        bordered={false}
      >
        <Input.Search
          placeholder="Tìm kiếm theo tên khách thuê"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          onSearch={handleSearch}
          allowClear
          style={{ marginBottom: 16,width: '50%',marginRight: 10 }}
          size={isMobile ? "small" : "middle"}
        />
        <Space>
          
        <Button
          icon={<ClearOutlined />}
          onClick={handleClearSearch}
          size={isMobile ? "small" : "middle"}
          style={{backgroundColor: 'red',color: 'white',borderRadius: 5}}
        >
          {!isMobile && "Xóa Bộ Lọc"}
        </Button>
        </Space>
        <Table
          columns={columns}
          dataSource={data.items}
          rowKey="TenantID"
          scroll={{
            x: isMobile ? 960 : 1200
          }}
          pagination={{
            current: data.currentPage,
            pageSize: queryParams.limit,
            total: data.total,
            showSizeChanger: true,
            showQuickJumper: !isMobile,
            showTotal: (total) => `Tổng số ${total} bản ghi`,
            onChange: (page, pageSize) => {
              setQueryParams(prev => ({
                ...prev,
                page: page,
                limit: pageSize
              }));
            }
          }}
          loading={loading}
          size={isMobile ? "small" : "middle"}
          tableLayout="fixed"
          style={{ overflowX: 'auto' }}
        />
      </Card>
      <TenantForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        initialValues={editingTenant}
        title={editingTenant ? 'Sửa thông tin khách thuê' : 'Thêm khách thuê mới'}
        industries={industries}
        factories={factories}
      />
      <Modal
        title="Thông báo của khách hàng"
        open={notificationModalVisible}
        onCancel={() => setNotificationModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setNotificationModalVisible(false)}>
            Đóng
          </Button>
        ]}
      >
        {selectedTenant && (
          <div>
            <Typography.Title level={5}>Mã khách hàng: {selectedTenant.TenantID}</Typography.Title>
            <Typography.Title level={5}>Tên khách hàng: {selectedTenant.TenantName}</Typography.Title>
            <List
              loading={loadingNotifications}
              itemLayout="vertical"
              dataSource={notifications}
              pagination={false}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{item.Title}</span>
                        <Tag color={item.IsRead ? 'green' : 'blue'}>
                          {item.IsRead ? 'Đã đọc' : 'Chưa đọc'}
                        </Tag>
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          <Tag color={item.Type === 'message' ? 'purple' : 'orange'}>
                            {item.Type === 'message' ? 'Thông báo' : 'Thanh toán'}
                          </Tag>
                          <span style={{ marginLeft: 8, color: '#666' }}>
                            {new Date(new Date(item.CreatedAt).getTime() - (7 * 60 * 60 * 1000)).toLocaleString('vi-VN', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false
                            })}
                          </span>
                        </div>
                        <div
                          dangerouslySetInnerHTML={{ __html: item.Content }}
                          style={{
                            maxHeight: '200px',
                            overflow: 'auto',
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px'
                          }}
                        />
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Pagination
                current={notificationPage}
                pageSize={notificationPageSize}
                total={notificationTotal}
                onChange={handleNotificationPageChange}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default TenantList; 